import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function Section({ section, answers, updateAnswer, isActive, onNext, onBack, isFirst, isLast, validationErrors }) {
  if (!isActive) return null;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        onNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="brief-section"
    >
      <div className="brief-section-title">{section.title}</div>
      {section.intro && (
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="brief-section-intro"
        >
          {section.intro}
        </motion.div>
      )}
      
      <div className="brief-questions">
        {section.questions.map((q, i) => (
          <Question 
            key={q.id} 
            question={q} 
            value={answers[q.id]} 
            onChange={(val) => updateAnswer(q.id, val)}
            error={validationErrors[q.id]}
            index={i}
            onNext={onNext}
          />
        ))}
      </div>

      <div className="brief-nav">
        {!isFirst ? (
          <button className="brief-btn brief-btn-secondary" onClick={onBack}>
            Back
          </button>
        ) : <div />}
        
        <button className="brief-btn brief-btn-primary" onClick={onNext}>
          {isLast ? "Submit Brief" : "Next Section"}
        </button>
      </div>
    </motion.div>
  );
}

function Question({ question, value, onChange, error, index, onNext }) {
  const [wordCount, setWordCount] = useState(0);
  
  useEffect(() => {
    if (typeof value === 'string') {
      setWordCount(value.trim() ? value.trim().split(/\s+/).length : 0);
    }
  }, [value]);

  const renderInput = () => {
    switch (question.type) {
      case "text":
        return (
          <input 
            type="text" 
            className="brief-input" 
            value={value || ""} 
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer..."
          />
        );
      case "longtext":
        return (
          <div className="brief-input-wrapper">
            <textarea 
              className="brief-textarea" 
              value={value || ""} 
              onChange={(e) => onChange(e.target.value)}
              placeholder="Tell us more..."
            />
            <div className={`brief-word-count ${wordCount > 400 ? "near-limit" : ""}`}>
              {wordCount} / 500 — short answers are fine
            </div>
          </div>
        );
      case "single-select":
        return (
          <div className="brief-options-grid">
            {question.options.map((opt) => (
              <motion.div 
                key={opt} 
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`brief-option ${value === opt ? "selected" : ""}`}
                onClick={() => onChange(opt)}
              >
                <div className="brief-option-check">
                  <AnimatePresence>
                    {value === opt && (
                      <motion.svg 
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      >
                        <path d="M20 6L9 17L4 12" />
                      </motion.svg>
                    )}
                  </AnimatePresence>
                </div>
                <span>{opt}</span>
              </motion.div>
            ))}
          </div>
        );
      case "multi-select":
        const currentVals = Array.isArray(value) ? value : [];
        const toggle = (opt) => {
          if (currentVals.includes(opt)) {
            onChange(currentVals.filter(v => v !== opt));
          } else {
            onChange([...currentVals, opt]);
          }
        };
        return (
          <div className="brief-options-grid">
            {question.options.map((opt) => (
              <motion.div 
                key={opt} 
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`brief-option ${currentVals.includes(opt) ? "selected" : ""}`}
                onClick={() => toggle(opt)}
              >
                <div className="brief-option-check">
                  <AnimatePresence>
                    {currentVals.includes(opt) && (
                      <motion.svg 
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      >
                        <path d="M20 6L9 17L4 12" />
                      </motion.svg>
                    )}
                  </AnimatePresence>
                </div>
                <span>{opt}</span>
              </motion.div>
            ))}
          </div>
        );
      case "scale":
        return (
          <div>
            <div className="brief-scale">
              {Array.from({ length: question.max - question.min + 1 }, (_, i) => i + question.min).map(n => (
                <div 
                  key={n} 
                  className={`brief-scale-item ${value === n ? "selected" : ""}`}
                  onClick={() => onChange(n)}
                >
                  {n}
                </div>
              ))}
            </div>
            <div className="brief-scale-labels">
              <span>{question.minLabel}</span>
              <span>{question.maxLabel}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
      className="brief-question"
    >
      <label className="brief-label">{question.label}</label>
      {question.help && <span className="brief-help">{question.help}</span>}
      {renderInput()}
      {!question.required && !value && (
        <div className="brief-skip-link" onClick={onNext}>Skip for now</div>
      )}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="brief-validation-msg"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
